/**
 * MatchCard Component - Compact Stitch Design
 * Horizontal compact match card with inline score display
 * Supports both reported and pending states
 * Uses Match Result Resolver pattern to display points consistent with standings
 */

import type { Match } from "../types/index";
import { resolveMatchPoints } from "../services/matchResultResolver";
import { BYE_POINTS } from "shared";
import { teamDisplayName, matchParticipants } from "../utils/matchTeams";

export interface MatchCardConfig {
  match: Match;
  courtNumber: number;
  leagueId: number;
  onReportClick?: (match: Match) => void;
  onEditClick?: (match: Match) => void;
  showRankings?: Map<number, number>; // playerId -> rank
}

/**
 * Renders a compact horizontal match card
 * Uses Stitch design with:
 * - Left side: Court number
 * - Middle: Team names and scores
 * - Right side: Status badge and action button
 * Points displayed are calculated using resolveMatchPoints() for consistency with standings
 */
export function renderMatchCard(config: MatchCardConfig): string {
  const {
    match,
    courtNumber,
    leagueId: _leagueId,
    showRankings: _showRankings = new Map()
  } = config;

  // Check if this is a Bye match
  const isBye = (match as any).isBye === true || match.matchType === "bye";
  const isReported = match.status === "reported";
  
  if (isBye) {
    // Handle remainder bye matches (1, 2, or 3 players) with unified display.
    // Participants come from the teams/teamNames structure (source of truth).
    const participants = matchParticipants(match);
    const allParticipantIds = participants.map((p) => p.id);
    const participantNames = participants.map((p) => p.name);

    // All participants in a bye group each receive BYE_POINTS (the same value
    // standings award) — kept in sync via the shared constant.
    const byePoints = allParticipantIds.length > 0 ? BYE_POINTS : 0;
    
    // Build participant list (handles 1, 2, or 3 players)
    const participantsList = participantNames
      .map((name) => `
        <div class="flex items-center gap-2 py-1.5 px-2 bg-slate-50 dark:bg-slate-800/50 daylight:bg-day-sunken rounded">
          <span class="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0"></span>
          <span class="font-semibold text-slate-800 dark:text-white daylight:text-day-ink text-sm">${name}</span>
        </div>
      `)
      .join('');

    return `
      <div class="group bg-white dark:bg-card-dark daylight:bg-day-card border border-slate-200 dark:border-slate-800 daylight:border-day-ink/10 rounded-xl p-3 flex flex-col md:flex-row items-start md:items-center gap-4 transition-all hover:shadow-lg daylight:hover:shadow-day-lg hover:border-slate-300 dark:hover:border-slate-700 daylight:hover:border-day-ink/20">
        <!-- Court Number Section -->
        <div class="flex flex-col items-center justify-center px-4 border-r border-slate-200 dark:border-slate-800 daylight:border-day-ink/10 min-w-[80px]">
          <span class="text-[10px] font-bold uppercase tracking-tighter text-slate-400 daylight:text-day-ink-muted">Court</span>
          <span class="text-xl font-black text-slate-900 dark:text-white">${courtNumber
            .toString()
            .padStart(2, "0")}</span>
        </div>

        <!-- Bye Participants Section -->
        <div class="w-full md:w-5/12 flex flex-col gap-2 min-w-0">
          <div class="text-xs font-bold uppercase text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted mb-1">
            Bye Participants (${allParticipantIds.length} ${allParticipantIds.length === 1 ? 'player' : 'players'})
          </div>
          ${participantsList}
        </div>

        <!-- Bye Badge Section -->
        <div class="flex items-center gap-2 px-6 border-l border-slate-200 dark:border-slate-800 daylight:border-day-ink/10 hidden md:flex flex-shrink-0">
          <div class="bg-slate-100 dark:bg-slate-900/50 daylight:bg-day-sunken rounded-lg px-4 py-3 text-center">
            <span class="block text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft mb-1">Bye Award</span>
            <span class="block text-lg font-black text-slate-900 dark:text-white daylight:text-day-ink">+${byePoints} pts each</span>
          </div>
        </div>

        <!-- Status Section -->
        <div class="flex-1 flex items-center gap-4 pl-6 border-l border-slate-200 dark:border-slate-800 daylight:border-day-ink/10 w-full md:w-auto justify-between md:justify-end">
          <div class="text-right">
            <span class="block text-[10px] uppercase font-bold text-accent">Technical Bye</span>
            <span class="block text-[10px] text-slate-400">Auto-Reported</span>
          </div>
          <div class="px-3 py-1 bg-slate-100 dark:bg-slate-800 daylight:bg-day-sunken rounded-lg text-[10px] font-bold text-slate-600 dark:text-slate-300 daylight:text-day-ink-soft whitespace-nowrap">
            <span class="material-symbols-outlined text-sm align-middle mr-1" style="vertical-align: -2px;">check</span>
            Completed
          </div>
        </div>
      </div>
    `;
  }

  // Regular match rendering
  const teamAName = teamDisplayName(match, 0);
  const teamBName = teamDisplayName(match, 1);

  // Parse sets if they're a string
  let sets: any[] = [];
  if (isReported && match.sets) {
    sets = typeof match.sets === "string" ? JSON.parse(match.sets) : match.sets;
  }

  // Resolve match points using centralized resolver for consistency with standings
  const matchPoints = isReported ? resolveMatchPoints({
    id: match.id,
    status: match.status,
    isBye: (match as any).isBye,
    byePairIndex: (match as any).byePairIndex,
    sets: sets,
    parsedSets: sets,
  }) : null;

  // Determine winner
  let teamAWins = 0;
  let teamBWins = 0;
  sets.forEach((set) => {
    if (set.pair1score > set.pair2score) teamAWins++;
    else if (set.pair2score > set.pair1score) teamBWins++;
  });

  // Generate set score cells
  const setScoreCells = [0, 1, 2].map((setIndex) => {
    const set = sets[setIndex];
    if (!set) {
      // Empty set - not played yet
      return `
        <div class="flex flex-col gap-1">
          <div class="w-8 h-8 bg-slate-100 dark:bg-slate-900/50 daylight:bg-day-sunken rounded flex items-center justify-center font-bold text-slate-400 daylight:text-day-ink-muted">-</div>
          <div class="w-8 h-8 bg-slate-100 dark:bg-slate-900/50 daylight:bg-day-sunken rounded flex items-center justify-center font-bold text-slate-400 daylight:text-day-ink-muted">-</div>
        </div>
      `;
    }

    // Determine set winners
    const team1Wins = set.pair1score > set.pair2score;
    const team2Wins = set.pair2score > set.pair1score;

    return `
      <div class="flex flex-col gap-1">
        <div class="w-8 h-8 ${
          team1Wins ? "bg-primary text-navy-800 shadow-sm daylight:shadow-day-sm" : "bg-slate-100 dark:bg-slate-900/50 daylight:bg-day-sunken text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted"
        } rounded flex items-center justify-center font-black">${set.pair1score}</div>
        <div class="w-8 h-8 ${
          team2Wins ? "bg-accent text-slate-900 shadow-sm daylight:shadow-day-sm" : "bg-slate-100 dark:bg-slate-900/50 daylight:bg-day-sunken text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted"
        } rounded flex items-center justify-center font-black">${set.pair2score}</div>
      </div>
    `;
  });

  const statusLabel = isReported ? "Reported" : "Pending";

  return `
    <div class="group bg-white dark:bg-card-dark border border-slate-200 dark:border-slate-800 rounded-xl p-3 flex flex-col md:flex-row items-start md:items-center gap-4 transition-all hover:shadow-lg hover:border-slate-300 dark:hover:border-slate-700">
      <!-- Court Number Section -->
      <div class="flex flex-col items-center justify-center px-4 border-r border-slate-200 dark:border-slate-800 min-w-[80px]">
        <span class="text-[10px] font-bold uppercase tracking-tighter text-slate-400">Court</span>
        <span class="text-xl font-black text-slate-900 dark:text-white">${courtNumber
          .toString()
          .padStart(2, "0")}</span>
      </div>

      <!-- Teams Section -->
      <div class="w-full md:w-5/12 grid grid-cols-1 gap-1 min-w-0">
        <!-- Team A -->
        <div class="flex items-center gap-3">
          <div class="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0"></div>
          <span class="font-bold text-slate-800 dark:text-white daylight:text-day-ink text-sm truncate">${teamAName}</span>
          ${
            isReported && teamAWins > teamBWins
              ? '<span class="material-symbols-outlined text-accent text-sm flex-shrink-0">check_circle</span>'
              : ""
          }
          ${
            matchPoints
              ? `<span class="ml-auto text-xs font-bold text-primary daylight:text-day-ink bg-primary/10 daylight:bg-primary/20 px-2 py-1 rounded">+${matchPoints.teamAPoints} pts</span>`
              : ""
          }
        </div>

        <!-- Team B -->
        <div class="flex items-center gap-3">
          <div class="w-1.5 h-1.5 rounded-full bg-accent flex-shrink-0"></div>
          <span class="font-bold text-slate-800 dark:text-white daylight:text-day-ink text-sm truncate">${teamBName}</span>
          ${
            isReported && teamBWins > teamAWins
              ? '<span class="material-symbols-outlined text-accent text-sm flex-shrink-0">check_circle</span>'
              : ""
          }
          ${
            matchPoints
              ? `<span class="ml-auto text-xs font-bold text-accent daylight:text-day-success bg-accent/10 daylight:bg-day-success/[0.14] px-2 py-1 rounded">+${matchPoints.teamBPoints} pts</span>`
              : ""
          }
        </div>
      </div>

      <!-- Scores Section -->
      <div class="flex items-center gap-2 px-6 border-l border-slate-200 dark:border-slate-800 daylight:border-day-ink/10 hidden md:flex flex-shrink-0">
        ${setScoreCells.map((cellHtml, index) => `
          <div class="flex flex-col items-center">
            <span class="text-[9px] uppercase font-bold text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted mb-1 ${!sets[index] && index === 2 ? "opacity-30" : ""}">Set ${index + 1}</span>
            ${cellHtml}
          </div>
        `).join("")}
      </div>

      <!-- Status & Action Section -->
      <div class="flex-1 flex items-center gap-4 pl-6 border-l border-slate-200 dark:border-slate-800 daylight:border-day-ink/10 w-full md:w-auto justify-between md:justify-end">
        <div class="text-right">
          <span class="block text-[10px] uppercase font-bold ${
            isReported ? "text-accent" : "text-warning"
          }">${statusLabel}</span>
          <span class="block text-[10px] text-slate-400">${isReported ? "Reported" : "Not started"}</span>
        </div>
        <button class="report-match-btn flex items-center gap-2 border ${
          isReported
            ? "bg-slate-100 dark:bg-slate-800 daylight:bg-day-sunken border-slate-200 dark:border-slate-700 daylight:border-day-ink/10 text-slate-500 daylight:text-day-ink-soft hover:bg-slate-200 dark:hover:bg-slate-700 daylight:hover:bg-day-void"
            : "bg-slate-100 dark:bg-slate-800 daylight:bg-day-sunken border-slate-200 dark:border-slate-700 daylight:border-day-ink/10 hover:bg-primary hover:text-navy-800 hover:border-primary text-slate-600 dark:text-slate-300 daylight:text-day-ink-soft"
        } px-4 py-2 rounded-lg font-bold text-sm transition-colors" data-match-id="${match.id}">
          <span class="material-symbols-outlined text-sm">${
            isReported ? "edit" : "assignment"
          }</span>
          <span>${isReported ? "Edit" : "Report"}</span>
        </button>
      </div>
    </div>
  `;
}

/**
 * Create a MatchCard class for integration with views
 */
export class MatchCard {
  static render(config: MatchCardConfig): string {
    return renderMatchCard(config);
  }
}